// src/admin/components/services/ServiceStatistics.jsx
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  RiFileChartLine, 
  RiArrowLeftLine,
  RiCalendarLine,
  RiBuildingLine,
  RiMoneyDollarCircleLine,
  RiErrorWarningLine,
  RiPieChartLine,
  RiBarChartGroupedLine,
  RiPercentLine,
  RiCheckLine,
  RiCloseCircleLine,
  RiAlertLine,
  RiAddLine,
  RiFileExcel2Line,
  RiDownload2Line
} from 'react-icons/ri';
import { useTheme } from '../../../contexts/ThemeContext';
import { 
  getServiceStats,
  formatCurrency,
  getMonthName,
  generateServicePayment
} from '../../../models/serviceModel';
import { getAllBuildings } from '../../../models/buildingModel';
import * as XLSX from 'xlsx';

const ServiceStatistics = () => {
  const { t, i18n } = useTranslation();
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isRTL = i18n.language === 'ar' || i18n.language === 'ku';
  
  // Parse query parameters
  const queryParams = new URLSearchParams(location.search);
  const initialBuildingId = queryParams.get('buildingId') || '';
  const initialYear = queryParams.get('year') || new Date().getFullYear();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [buildings, setBuildings] = useState([]);
  const [selectedBuilding, setSelectedBuilding] = useState(initialBuildingId);
  const [selectedYear, setSelectedYear] = useState(initialYear);
  const [stats, setStats] = useState({
    overallStats: {},
    monthlyStats: []
  });
  const [exportLoading, setExportLoading] = useState(false);
  
  // Available years for filter (current year and 3 previous years)
  const currentYear = new Date().getFullYear();
  const availableYears = Array.from({ length: 4 }, (_, i) => currentYear - i);
  
  // Load buildings
  useEffect(() => {
    const fetchBuildings = async () => {
      try {
        const response = await getAllBuildings();
        setBuildings(response.buildings || []);
        
        // If no building is selected but we have buildings, select the first one
        if (!selectedBuilding && response.buildings && response.buildings.length > 0) {
          setSelectedBuilding(response.buildings[0].id);
        }
      } catch (err) {
        console.error('Error loading buildings:', err);
        setError(t('failed_to_load_buildings'));
      }
    };
    
    fetchBuildings();
  }, [t]);
  
  // Load statistics when building or year changes
  useEffect(() => {
    if (!selectedBuilding) return;
    
    const fetchStats = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await getServiceStats(selectedBuilding, selectedYear);
        
        if (response.success) {
          setStats({
            overallStats: response.overallStats || {},
            monthlyStats: response.monthlyStats || []
          });
        } else {
          throw new Error(response.message || t('failed_to_load_service_statistics'));
        }
      } catch (err) {
        console.error('Error loading service statistics:', err);
        setError(err.message || t('failed_to_load_service_statistics'));
      } finally {
        setLoading(false);
      }
    };
    
    fetchStats();
  }, [selectedBuilding, selectedYear, t]);
  
  // Handle building change
  const handleBuildingChange = (e) => {
    setSelectedBuilding(e.target.value);
  };
  
  // Handle year change
  const handleYearChange = (e) => {
    setSelectedYear(parseInt(e.target.value));
  };
  
  // Calculate percentage
  const calculatePercentage = (value, total) => {
    if (!total) return 0;
    return Math.round((value / total) * 100);
  };

  // Get status indicator color
  const getStatusColor = (paymentRate) => {
    if (paymentRate >= 80) {
      return isDarkMode ? 'text-green-400' : 'text-green-600';
    } else if (paymentRate >= 50) {
      return isDarkMode ? 'text-yellow-400' : 'text-yellow-600';
    } else {
      return isDarkMode ? 'text-red-400' : 'text-red-600';
    }
  };

  // Function to refresh data
  const refreshData = async () => {
    if (!selectedBuilding) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const response = await getServiceStats(selectedBuilding, selectedYear);
      
      if (response.success) {
        setStats({
          overallStats: response.overallStats || {},
          monthlyStats: response.monthlyStats || []
        });
      } else {
        throw new Error(response.message || t('failed_to_load_service_statistics'));
      }
    } catch (err) {
      console.error('Error loading service statistics:', err);
      setError(err.message || t('failed_to_load_service_statistics'));
    } finally {
      setLoading(false);
    }
  };

  // Export to Excel function
  const exportToExcel = async () => {
    try {
      if (!selectedBuilding || !stats.monthlyStats) {
        setError(t('no_data_to_export') || 'No data available to export');
        return;
      }

      setExportLoading(true);
      setError(null);

      const buildingName = buildings.find(b => b.id === selectedBuilding)?.name || 'Building';
      const currentDateTime = new Date().toISOString().replace(/[:.]/g, '-');
      const fileName = `service_payments_${buildingName}_${selectedYear}_${currentDateTime}.xlsx`;

      // Create a new workbook
      const wb = XLSX.utils.book_new();

      // Prepare monthly data for export
      const monthlyData = Array.from({ length: 12 }, (_, i) => {
        const monthNum = i + 1;
        const monthData = stats.monthlyStats.find(m => m.month === monthNum) || null;
        const hasPayments = monthData && monthData.totalPayments > 0;
        
        return {
          [t('month') || 'Month']: t(getMonthName(monthNum).toLowerCase()) || getMonthName(monthNum),
          [t('status') || 'Status']: hasPayments ? t('generated') || 'Generated' : t('no_payments') || 'No Payments',
          [t('total_amount') || 'Total Amount']: monthData?.totalAmount || 0,
          [t('collected') || 'Collected']: monthData?.totalPaid || 0,
          [t('outstanding') || 'Outstanding']: monthData?.totalOutstanding || 0,
          [t('payment_rate') || 'Payment Rate']: hasPayments ? `${monthData?.paymentRate || 0}%` : '0%',
          [t('paid_count') || 'Paid Count']: monthData?.paidCount || 0,
          [t('unpaid_count') || 'Unpaid Count']: monthData?.unpaidCount || 0,
          [t('total_payments') || 'Total Payments']: monthData?.totalPayments || 0
        };
      });

      // Create a worksheet for monthly data
      const wsMonthly = XLSX.utils.json_to_sheet(monthlyData);
      
      // Set column widths
      const wscols = [
        { wch: 15 }, // Month
        { wch: 15 }, // Status
        { wch: 15 }, // Total Amount
        { wch: 15 }, // Collected
        { wch: 15 }, // Outstanding
        { wch: 15 }, // Payment Rate
        { wch: 15 }, // Paid Count
        { wch: 15 }, // Unpaid Count
        { wch: 15 }  // Total Payments
      ];
      wsMonthly['!cols'] = wscols;

      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(wb, wsMonthly, t('monthly_statistics') || 'Monthly Statistics');

      // Create summary worksheet
      const summaryData = [
        {
          [t('summary_item') || 'Summary Item']: t('building') || 'Building',
          [t('value') || 'Value']: buildingName
        },
        {
          [t('summary_item') || 'Summary Item']: t('year') || 'Year',
          [t('value') || 'Value']: selectedYear
        },
        {
          [t('summary_item') || 'Summary Item']: t('total_amount') || 'Total Amount',
          [t('value') || 'Value']: stats.overallStats.totalAmount || 0
        },
        {
          [t('summary_item') || 'Summary Item']: t('total_collected') || 'Total Collected',
          [t('value') || 'Value']: stats.overallStats.totalPaid || 0
        },
        {
          [t('summary_item') || 'Summary Item']: t('total_outstanding') || 'Total Outstanding',
          [t('value') || 'Value']: stats.overallStats.totalOutstanding || 0
        },
        {
          [t('summary_item') || 'Summary Item']: t('overall_payment_rate') || 'Overall Payment Rate',
          [t('value') || 'Value']: `${calculatePercentage(stats.overallStats.paidCount || 0, stats.overallStats.totalPayments || 0)}%`
        },
        {
          [t('summary_item') || 'Summary Item']: t('total_payments') || 'Total Payments',
          [t('value') || 'Value']: stats.overallStats.totalPayments || 0
        },
        {
          [t('summary_item') || 'Summary Item']: t('paid_payments') || 'Paid Payments',
          [t('value') || 'Value']: stats.overallStats.paidCount || 0
        },
        {
          [t('summary_item') || 'Summary Item']: t('unpaid_payments') || 'Unpaid Payments',
          [t('value') || 'Value']: stats.overallStats.unpaidCount || 0
        },
        {
          [t('summary_item') || 'Summary Item']: t('export_date') || 'Export Date',
          [t('value') || 'Value']: new Date().toLocaleString()
        }
      ];

      // Create a worksheet for summary data
      const wsSummary = XLSX.utils.json_to_sheet(summaryData);
      
      // Set column widths for summary
      wsSummary['!cols'] = [
        { wch: 25 }, // Summary Item
        { wch: 25 }  // Value
      ];

      // Add summary worksheet to workbook
      XLSX.utils.book_append_sheet(wb, wsSummary, t('summary') || 'Summary');

      // Write the workbook and trigger download
      XLSX.writeFile(wb, fileName);

      setSuccess(t('export_successful') || 'Data exported successfully');
      
      // Auto-hide success message after 3 seconds
      setTimeout(() => {
        setSuccess(null);
      }, 3000);
    } catch (err) {
      console.error('Error exporting to Excel:', err);
      setError(t('export_failed') || 'Failed to export data to Excel');
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <div className={`space-y-6 ${isRTL ? 'rtl' : 'ltr'}`}>
      {/* Header */}
      <div className={`${isDarkMode ? 'bg-zinc-900 border-gray-700' : 'bg-white border-gray-200'} rounded-xl border shadow-lg overflow-hidden`}>
        <div className={`p-6 ${isDarkMode ? 'bg-gray-750 border-b border-gray-700' : 'bg-purple-50 border-b border-gray-200'}`}>
          <div className="flex justify-between items-center">
            <div className="flex items-center">
              <RiFileChartLine className={`text-2xl ${isRTL ? 'ml-3' : 'mr-3'} ${isDarkMode ? 'text-purple-400' : 'text-purple-500'}`} />
              <h1 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                {t('service_payment_statistics')}
              </h1>
            </div>
            
            <div className="flex space-x-2">
              <button
                onClick={exportToExcel}
                disabled={loading || exportLoading || !selectedBuilding || stats.monthlyStats.length === 0}
                className={`p-2 rounded-full ${
                  isDarkMode ? 'bg-green-600 text-white hover:bg-green-700 disabled:bg-zinc-800 disabled:text-gray-500' : 'bg-green-500 text-white hover:bg-green-600 disabled:bg-gray-200 disabled:text-zinc-400'
                } ${exportLoading ? 'opacity-70 cursor-not-allowed' : ''}`}
                title={t('export_to_excel') || 'Export to Excel'}
              >
                {exportLoading ? (
                  <div className="h-5 w-5 animate-spin rounded-full border-2 border-current border-t-transparent"></div>
                ) : (
                  <RiFileExcel2Line size={20} />
                )}
              </button>
              
              <button
                onClick={() => navigate('/admin/services')}
                className={`p-2 rounded-full ${
                  isDarkMode ? 'bg-zinc-800 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
                title={t('back_to_services')}
              >
                <RiArrowLeftLine size={20} />
              </button>
            </div>
          </div>
        </div>

        {/* Controls */}
        <div className="p-6">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Building Selector */}
            <div className="w-full sm:w-64">
              <label className={`block mb-1 text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                {t('building')}
              </label>
              <select
                value={selectedBuilding}
                onChange={handleBuildingChange}
                className={`w-full p-2.5 rounded-lg border ${
                  isDarkMode ? 'bg-zinc-800 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                }`}
              >
                <option value="">{t('select_building')}</option>
                {buildings.map(building => (
                  <option key={building.id} value={building.id}>
                    {building.name}
                  </option>
                ))}
              </select>
            </div>
            
            {/* Year Selector */}
            <div className="w-full sm:w-auto">
              <label className={`block mb-1 text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                {t('year')}
              </label>
              <select
                value={selectedYear}
                onChange={handleYearChange}
                className={`w-full p-2.5 rounded-lg border ${
                  isDarkMode ? 'bg-zinc-800 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                }`}
              >
                {availableYears.map(year => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>
            
            {/* Export Button (for smaller screens) */}
            <div className="w-full sm:hidden flex items-end">
              <button
                onClick={exportToExcel}
                disabled={loading || exportLoading || !selectedBuilding || stats.monthlyStats.length === 0}
                className={`w-full p-2.5 rounded-lg flex justify-center items-center ${
                  isDarkMode ? 'bg-green-600 text-white hover:bg-green-700 disabled:bg-zinc-800 disabled:text-gray-500' : 'bg-green-500 text-white hover:bg-green-600 disabled:bg-gray-200 disabled:text-zinc-400'
                } ${exportLoading ? 'opacity-70 cursor-not-allowed' : ''}`}
              >
                {exportLoading ? (
                  <div className="h-5 w-5 animate-spin rounded-full border-2 border-current border-t-transparent mr-2"></div>
                ) : (
                  <RiFileExcel2Line size={18} className={`${isRTL ? 'ml-2' : 'mr-2'}`} />
                )}
                {t('export_to_excel') || 'Export to Excel'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Success Message */}
      {success && (
        <div className={`p-4 rounded-lg ${
          isDarkMode ? 'bg-green-900 bg-opacity-20 border-green-800 text-green-400' : 'bg-green-50 border-green-200 text-green-700'
        } border flex items-center`}>
          <RiCheckLine className={`${isRTL ? 'ml-2' : 'mr-2'} text-xl`} />
          {success}
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className={`p-4 rounded-lg ${
          isDarkMode ? 'bg-red-900 bg-opacity-20 border-red-800 text-red-400' : 'bg-zinc-50 border-red-200 text-red-700'
        } border flex items-center`}>
          <RiErrorWarningLine className={`${isRTL ? 'ml-2' : 'mr-2'} text-xl`} />
          {error}
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className={`${isDarkMode ? 'bg-zinc-900 border-gray-700' : 'bg-white border-gray-200'} rounded-xl border shadow-lg p-8 text-center`}>
          <div className="flex flex-col items-center justify-center space-y-4">
            <div className={`inline-block h-12 w-12 animate-spin rounded-full border-4 ${
              isDarkMode ? 'border-purple-500 border-t-transparent' : 'border-purple-500 border-t-transparent'
            }`}></div>
            <p className={`mt-2 text-lg font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              {t('loading_statistics')}
            </p>
          </div>
        </div>
      )}

      {/* No Building Selected */}
      {!selectedBuilding && !loading && (
        <div className={`${isDarkMode ? 'bg-zinc-900 border-gray-700' : 'bg-white border-gray-200'} rounded-xl border shadow-lg p-8 text-center`}>
          <RiBuildingLine className={`mx-auto text-6xl mb-4 ${isDarkMode ? 'text-gray-600' : 'text-zinc-400'}`} />
          <h3 className={`text-lg font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            {t('no_building_selected')}
          </h3>
          <p className={`mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            {t('please_select_building_statistics')}
          </p>
        </div>
      )}

      {/* Statistics Content */}
      {selectedBuilding && !loading && !error && (
        <div className="space-y-6">
          {/* Overall Statistics */}
          <div className={`${isDarkMode ? 'bg-zinc-900 border-gray-700' : 'bg-white border-gray-200'} rounded-xl border shadow-lg overflow-hidden`}>
            <div className={`p-4 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <h2 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                {t('overall_statistics')} - {selectedYear}
              </h2>
            </div>
            
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Total Collection */}
                <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-zinc-800' : 'bg-green-50'}`}>
                  <div className="flex items-center">
                    <div className={`p-3 rounded-full ${isDarkMode ? 'bg-green-900 bg-opacity-20' : 'bg-green-100'} ${isRTL ? 'ml-4' : 'mr-4'}`}>
                      <RiMoneyDollarCircleLine className={`text-2xl ${isDarkMode ? 'text-green-400' : 'text-green-600'}`} />
                    </div>
                    <div>
                      <p className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-gray-600'}`}>
                        {t('total_collection')}
                      </p>
                      <p className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        {formatCurrency(stats.overallStats.totalPaid || 0)}
                      </p>
                      <p className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-gray-600'}`}>
                        {t('of')} {formatCurrency(stats.overallStats.totalAmount || 0)}
                      </p>
                    </div>
                  </div>
                  
                  {/* Progress Bar */}
                  <div className={`mt-4 h-2 w-full rounded-full ${isDarkMode ? 'bg-gray-600' : 'bg-green-100'}`}>
                    <div 
                      className={`h-2 rounded-full ${isDarkMode ? 'bg-green-500' : 'bg-green-500'}`}
                      style={{ 
                        width: `${calculatePercentage(stats.overallStats.totalPaid || 0, stats.overallStats.totalAmount || 0)}%` 
                      }}
                    ></div>
                  </div>
                </div>
                
                {/* Payment Rate */}
                <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-zinc-800' : 'bg-zinc-50'}`}>
                  <div className="flex items-center">
                    <div className={`p-3 rounded-full ${isDarkMode ? 'bg-red-900 bg-opacity-20' : 'bg-zinc-100'} ${isRTL ? 'ml-4' : 'mr-4'}`}>
                      <RiPercentLine className={`text-2xl ${isDarkMode ? 'text-red-400' : 'text-red-600'}`} />
                    </div>
                    <div>
                      <p className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-gray-600'}`}>
                        {t('payment_rate')}
                      </p>
                      <p className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        {calculatePercentage(stats.overallStats.paidCount || 0, stats.overallStats.totalPayments || 0)}%
                      </p>
                      <p className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-gray-600'}`}>
                        {stats.overallStats.paidCount || 0} {t('of')} {stats.overallStats.totalPayments || 0} {t('payments')}
                      </p>
                    </div>
                  </div>
                  
                  {/* Progress Bar */}
                  <div className={`mt-4 h-2 w-full rounded-full ${isDarkMode ? 'bg-gray-600' : 'bg-zinc-100'}`}>
                    <div 
                      className={`h-2 rounded-full ${isDarkMode ? 'bg-zinc-500' : 'bg-zinc-500'}`}
                      style={{ 
                        width: `${calculatePercentage(stats.overallStats.paidCount || 0, stats.overallStats.totalPayments || 0)}%` 
                      }}
                    ></div>
                  </div>
                </div>
                
                {/* Outstanding Amount */}
                <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-zinc-800' : 'bg-zinc-50'}`}>
                  <div className="flex items-center">
                    <div className={`p-3 rounded-full ${isDarkMode ? 'bg-red-900 bg-opacity-20' : 'bg-zinc-100'} ${isRTL ? 'ml-4' : 'mr-4'}`}>
                      <RiCloseCircleLine className={`text-2xl ${isDarkMode ? 'text-red-400' : 'text-red-600'}`} />
                    </div>
                    <div>
                      <p className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-gray-600'}`}>
                        {t('outstanding_amount')}
                      </p>
                      <p className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        {formatCurrency(stats.overallStats.totalOutstanding || 0)}
                      </p>
                      <p className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-gray-600'}`}>
                        {stats.overallStats.unpaidCount || 0} {t('unpaid_payments')}
                      </p>
                    </div>
                  </div>
                  
                  {/* Progress Bar */}
                  <div className={`mt-4 h-2 w-full rounded-full ${isDarkMode ? 'bg-gray-600' : 'bg-zinc-100'}`}>
                    <div 
                      className={`h-2 rounded-full ${isDarkMode ? 'bg-zinc-500' : 'bg-zinc-500'}`}
                      style={{ 
                        width: `${calculatePercentage(stats.overallStats.totalOutstanding || 0, stats.overallStats.totalAmount || 0)}%` 
                      }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Monthly Statistics */}
          <div className={`${isDarkMode ? 'bg-zinc-900 border-gray-700' : 'bg-white border-gray-200'} rounded-xl border shadow-lg overflow-hidden`}>
            <div className={`p-4 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'} flex justify-between items-center`}>
              <h2 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                {t('monthly_statistics')} - {selectedYear}
              </h2>
              
              {/* Export button in table header (visible on larger screens) */}
              <button
                onClick={exportToExcel}
                disabled={loading || exportLoading || stats.monthlyStats.length === 0}
                className={`hidden sm:flex items-center px-3 py-1.5 rounded-lg text-sm ${
                  isDarkMode ? 'bg-green-600 text-white hover:bg-green-700 disabled:bg-zinc-800 disabled:text-gray-500' : 'bg-green-500 text-white hover:bg-green-600 disabled:bg-gray-200 disabled:text-zinc-400'
                } ${exportLoading ? 'opacity-70 cursor-not-allowed' : ''}`}
              >
                {exportLoading ? (
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent mr-2"></div>
                ) : (
                  <RiFileExcel2Line size={16} className={`${isRTL ? 'ml-2' : 'mr-2'}`} />
                )}
                {t('export_to_excel') || 'Export to Excel'}
              </button>
            </div>
            
            <div className="p-6 overflow-x-auto">
              {stats.monthlyStats.length === 0 ? (
                <div className="text-center py-6">
                  <p className={`${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    {t('no_monthly_data')}
                  </p>
                </div>
              ) : (
                <table className={`min-w-full ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  <thead className={isDarkMode ? 'border-b border-gray-700' : 'border-b border-gray-200'}>
                    <tr>
                      <th className={`px-3 py-3 ${isRTL ? 'text-right' : 'text-left'}`}>{t('month')}</th>
                      <th className={`px-3 py-3 text-center`}>{t('status')}</th>
                      <th className={`px-3 py-3 ${isRTL ? 'text-left' : 'text-right'}`}>{t('total_amount')}</th>
                      <th className={`px-3 py-3 ${isRTL ? 'text-left' : 'text-right'}`}>{t('total_amount')}</th>
                      <th className={`px-3 py-3 ${isRTL ? 'text-left' : 'text-right'}`}>{t('collected')}</th>
                      <th className={`px-3 py-3 ${isRTL ? 'text-left' : 'text-right'}`}>{t('outstanding')}</th>
                      <th className={`px-3 py-3 ${isRTL ? 'text-left' : 'text-right'}`}>{t('payment_rate')}</th>
                      <th className="px-3 py-3 text-center">{t('progress')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* All 12 months of the year */}
                    {Array.from({ length: 12 }, (_, i) => i + 1).map(monthNum => {
                      // Find month in stats if it exists
                      const monthData = stats.monthlyStats.find(m => m.month === monthNum) || null;
                      const hasPayments = monthData && monthData.totalPayments > 0;
                      
                      return (
                        <tr 
                          key={monthNum} 
                          className={`${isDarkMode ? 'border-b border-gray-700' : 'border-b border-gray-200'} hover:bg-opacity-10 hover:bg-purple-500 ${
                            hasPayments ? '' : (isDarkMode ? 'text-gray-500' : 'text-zinc-400')
                          }`}
                        >
                          <td className="px-3 py-3">
                            <div className="font-medium">{t(getMonthName(monthNum).toLowerCase())}</div>
                          </td>
                          <td className="px-3 py-3 text-center">
                            {hasPayments ? (
                              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                isDarkMode ? 'bg-green-900 bg-opacity-20 text-green-400' : 'bg-green-100 text-green-700'
                              }`}>
                                <RiCheckLine className={`${isRTL ? 'ml-1' : 'mr-1'}`} size={14} />
                                {t('generated')}
                              </span>
                            ) : (
                              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                isDarkMode ? 'bg-zinc-800 text-zinc-400' : 'bg-gray-100 text-gray-500'
                              }`}>
                                <RiAlertLine className={`${isRTL ? 'ml-1' : 'mr-1'}`} size={14} />
                                {t('no_payments')}
                              </span>
                            )}
                          </td>
                          <td className={`px-3 py-3 ${isRTL ? 'text-left' : 'text-right'}`}>
                            {formatCurrency(monthData?.totalAmount || 0)}
                          </td>
                          <td className={`px-3 py-3 ${isRTL ? 'text-left' : 'text-right'}`}>
                            {hasPayments && (
                              <span className={isDarkMode ? 'text-green-400' : 'text-green-600'}>
                                {formatCurrency(monthData?.totalPaid || 0)}
                              </span>
                            )}
                          </td>
                          <td className={`px-3 py-3 ${isRTL ? 'text-left' : 'text-right'}`}>
                            {hasPayments && (
                              <span className={isDarkMode ? 'text-red-400' : 'text-red-600'}>
                                {formatCurrency(monthData?.totalOutstanding || 0)}
                              </span>
                            )}
                          </td>
                          <td className={`px-3 py-3 ${isRTL ? 'text-left' : 'text-right'}`}>
                            {hasPayments ? (
                              <span className={getStatusColor(monthData?.paymentRate || 0)}>
                                {monthData?.paymentRate || 0}%
                              </span>
                            ) : '-'}
                          </td>
                          <td className="px-3 py-3">
                            {hasPayments ? (
                              <div className={`h-2 w-full rounded-full ${isDarkMode ? 'bg-gray-600' : 'bg-gray-200'}`}>
                                <div 
                                  className={`h-2 rounded-full ${
                                    monthData?.paymentRate >= 80 
                                      ? (isDarkMode ? 'bg-green-500' : 'bg-green-500') 
                                      : monthData?.paymentRate >= 50 
                                        ? (isDarkMode ? 'bg-yellow-500' : 'bg-yellow-500')
                                        : (isDarkMode ? 'bg-zinc-500' : 'bg-zinc-500')
                                  }`}
                                  style={{ width: `${monthData?.paymentRate || 0}%` }}
                                ></div>
                              </div>
                            ) : (
                              <div className="flex justify-center">
                                <button
                                  onClick={() => navigate(`/admin/services/generate?month=${monthNum}&year=${selectedYear}&buildingId=${selectedBuilding}`)}
                                  className={`px-2 py-1 rounded text-xs ${
                                    isDarkMode ? 'bg-red-600 text-white hover:bg-red-700' : 'bg-zinc-500 text-white hover:bg-red-600'
                                  }`}
                                >
                                  <RiAddLine className={`${isRTL ? 'ml-1' : 'mr-1'} inline`} size={12} />
                                  {t('generate')}
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
            
            {/* Export Reminder - only shown when there's data and on mobile */}
            {stats.monthlyStats.length > 0 && (
              <div className="px-6 pb-4 sm:hidden">
                <div className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-gray-600'} flex items-center justify-center`}>
                  <RiDownload2Line className={`${isRTL ? 'ml-1' : 'mr-1'}`} size={14} />
                  {t('export_data_reminder') || 'You can export this data to Excel using the export button above'}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ServiceStatistics;